/**
 * RateLimiter - TypeScript implementation
 * Follows registry pattern with explicit wiring
 * FALLBACK URL HAS NO LIMITS
 */

import type {
  StateSchema,
  StateLimitsSchema,
  UrlData,
  MethodLimits,
  RateLimitEntry,
  DefaultLimits,
  SavedState,
  RpcPayload
} from './types.js';

import { getJsonSizeInMb } from './utils/sizeUtils.js';
import {
  getResponseHeaders,
  getRetryAfter,
  getRemainingMethod,
  getMethodRateLimit,
  getRpsLimit
} from './utils/headerUtils.js';
import { getRpcPayload } from './utils/rpcUtils.js';
import { getCurrentTime } from './utils/timeUtils.js';
import { cleanQueries, getIsLimit } from './utils/limitUtils.js';
import {
  safeDumpToFile,
  safeLoadFromJson,
  getSaveStatePath,
  getRateLimitsPath
} from './utils/fileUtils.js';

const RPC_URL = "https://api.mainnet-beta.solana.com";
const FALLBACK_URL = 'https://rpc.ankr.com/solana/c3b7fd92e298d5682b6ef095eaa4e92160989a713f5ee9ac2693b4da8ff5a370';

/**
 * Helper function to add values to list in dict
 */
function addToList(
  dictObj: Record<string, string[]>,
  key: string,
  values: string | string[]
): Record<string, string[]> {
  const valueArray = Array.isArray(values) ? values : [values];
  
  if (!(key in dictObj)) {
    dictObj[key] = [];
  }
  
  for (const value of valueArray) {
    if (!dictObj[key].includes(value)) {
      dictObj[key].push(value);
    }
  }
  
  return dictObj;
}

/**
 * Build URL registry from list of URLs
 */
function getBaseUrls(urls: string[], statePath: string): StateSchema {
  const allUrls: Record<string, UrlData> = {};
  const state = safeLoadFromJson<SavedState>(statePath);
  const allBase = state?.all_base || {};
  
  const urlsJs: StateSchema = {
    last_method: state?.last_method || null,
    last_url: state?.last_url || null,
    timed_method: null,
    all_base: allBase,
    all_urls: allUrls
  };
  
  for (const url of urls) {
    const parsed = new URL(url);
    const netloc = parsed.hostname;
    const lastDotIndex = netloc.lastIndexOf('.');
    const name = lastDotIndex > 0 ? netloc.substring(0, lastDotIndex) : netloc;
    const ext = lastDotIndex > 0 ? netloc.substring(lastDotIndex) : '';
    
    if (!(netloc in urlsJs.all_urls)) {
      urlsJs.all_urls[netloc] = {
        scheme: parsed.protocol.replace(':', ''),
        netloc: netloc,
        name: name,
        ext: ext,
        rate_limits: [],
        last_mb: {},
        cooldown_times: {}
      };
    }
    
    addToList(allBase, netloc, [name, url]);
    addToList(allBase, url, [netloc, name]);
    addToList(allBase, name, [netloc, url]);
  }
  
  urlsJs.all_base = allBase;
  return urlsJs;
}

/**
 * Singleton RateLimiter class
 */
export class RateLimiter {
  private static instance: RateLimiter;

  private initialized: boolean = false;

  private urls!: string[];
  private fallback_url!: string;
  private fallback_netloc: string | null = null;

  private state_file!: string;
  private state_limits_file!: string;

  private state!: StateSchema;
  private state_limits!: StateLimitsSchema;

  private timed_method: string | null = null;
  private headers: Record<string, string> = {};
  private payload: RpcPayload | null = null;


  constructor(
    urls?: string[],
    fallbackRpcUrl?: string,
    envDirectory?: string,
    saveStatePath?: string,
    saveLimitsPath?: string
  ) {
    if (RateLimiter.instance && RateLimiter.instance.initialized) {
      return RateLimiter.instance;
    }

    this.fallback_url = fallbackRpcUrl || FALLBACK_URL;
    this.urls = urls || [RPC_URL, this.fallback_url];
    
    this.state_file = saveStatePath || getSaveStatePath();
    this.state_limits_file = saveLimitsPath || getRateLimitsPath();
    
    // Initialize state registry
    this.state = getBaseUrls(this.urls, this.state_file);
    this.state_limits = this._loadStateLimits();
    
    // Cache fallback netloc for quick lookup
    this.fallback_netloc = this._resolveNetloc(this.fallback_url);
    
    this.initialized = true;
    
    RateLimiter.instance = this;
  }

  /**
   * Get singleton instance
   */
  static getInstance(): RateLimiter {
    if (!RateLimiter.instance) {
      RateLimiter.instance = new RateLimiter();
    }
    return RateLimiter.instance;
  }

  /**
   * Check if identifier is the fallback URL/netloc
   */
  private _isFallback(identifier: string): boolean {
    const netloc = this._resolveNetloc(identifier);
    return netloc === this.fallback_netloc;
  }

  /**
   * Load rate limits from persistent storage
   */
  private _loadStateLimits(): StateLimitsSchema {
    const limits = safeLoadFromJson<StateLimitsSchema>(this.state_limits_file) || {};
    
    // Ensure all netlocs have limit entries
    for (const netloc of Object.keys(this.state.all_urls)) {
      if (!(netloc in limits)) {
        limits[netloc] = {};
      }
    }
    
    return limits;
  }

  /**
   * Resolve any identifier (url, netloc, name) to netloc
   */
  private _resolveNetloc(identifier: string): string | null {
    if (identifier in this.state.all_urls) {
      return identifier;
    }
    
    if (identifier in this.state.all_base) {
      const resolved = this.state.all_base[identifier];
      for (const item of resolved) {
        if (item in this.state.all_urls) {
          return item;
        }
      }
    }
    
    // Try parsing as URL
    try {
      const parsed = new URL(identifier);
      if (parsed.hostname in this.state.all_urls) {
        return parsed.hostname;
      }
    } catch (e) {
      // Not a valid URL, continue
    }
    
    return null;
  }

  /**
   * Get full URL from netloc
   */
  private _resolveUrl(netloc: string): string | null {
    if (!(netloc in this.state.all_urls)) {
      return null;
    }
    
    const urlData = this.state.all_urls[netloc];
    
    // Look it up in all_base
    if (netloc in this.state.all_base) {
      for (const item of this.state.all_base[netloc]) {
        if (item.startsWith('http')) {
          return item;
        }
      }
    }
    
    // Fallback: reconstruct from scheme and netloc
    return `${urlData.scheme}://${netloc}`;
  }

  /**
   * Get URL data structure for any identifier
   */
  private _getUrlData(identifier: string): UrlData | null {
    const netloc = this._resolveNetloc(identifier);
    if (!netloc) {
      return null;
    }
    return this.state.all_urls[netloc];
  }

  /**
   * Save the current state of the rate limiter
   */
  saveState(): void {
    const state: SavedState = {
      last_method: this.state.last_method,
      last_url: this.state.last_url,
      all_base: this.state.all_base
    };
    safeDumpToFile(state, this.state_file);
  }

  /**
   * Ensure method has limit structure in state_limits
   */
  private _ensureMethodLimits(netloc: string, method: string): void {
    if (!(method in this.state_limits[netloc])) {
      this.state_limits[netloc][method] = {
        rate_limit: null,
        rps_limit: null,
        retry_after: null,
        avg_data: null,
        last_data: null
      };
      safeDumpToFile(this.state_limits, this.state_limits_file);
    }
  }

  /**
   * Extract and persist headers from response
   */
  getHeaders(
    response: Response,
    method: string,
    identifier: string
  ): [number, number] | [null, null] {
    const netloc = this._resolveNetloc(identifier);
    if (!netloc) {
      return [null, null];
    }
    
    // Skip rate limiting for fallback
    if (this._isFallback(netloc)) {
      //console.log(`Fallback URL - no rate limits applied`);
      return [0, -1];
    }
    
    const headers = getResponseHeaders(response);
    this._ensureMethodLimits(netloc, method);
    
    // Extract and update rate limit
    const rateLimit = getMethodRateLimit(response);
    if (rateLimit && this.state_limits[netloc][method].rate_limit !== rateLimit) {
      this.state_limits[netloc][method].rate_limit = rateLimit;
      safeDumpToFile(this.state_limits, this.state_limits_file);
    }
    
    // Extract and update RPS limit
    const rpsLimit = getRpsLimit(response);
    if (rpsLimit && this.state_limits[netloc][method].rps_limit !== rpsLimit) {
      this.state_limits[netloc][method].rps_limit = rpsLimit;
      safeDumpToFile(this.state_limits, this.state_limits_file);
    }
    
    // Extract and update retry after
    const retryAfter = getRetryAfter(response);
    if (retryAfter && this.state_limits[netloc][method].retry_after !== retryAfter) {
      this.state_limits[netloc][method].retry_after = retryAfter;
      safeDumpToFile(this.state_limits, this.state_limits_file);
    }
    
    // Check remaining and set cooldown if needed
    const methodRemaining = getRemainingMethod(response);
    if (methodRemaining === 0) {
      if (!this.getCooldownForMethod(netloc, method)) {
        const coolDown = this.state_limits[netloc][method].retry_after || 1;
        this.setCooldown(netloc, method, Math.max(coolDown, 0));
      }
    }
    
    return [retryAfter, methodRemaining];
  }

  /**
   * Get rate limit value from state with defaults
   */
  getStateRateLimit(
    method: string,
    identifier: string,
    limitType: keyof MethodLimits
  ): number {
    const defaultLimits: DefaultLimits = {
      rate_limit: 40,
      rps_limit: 100,
      retry_after: 10
    };
    
    const netloc = this._resolveNetloc(identifier);
    if (!netloc) {
      return defaultLimits[limitType as keyof DefaultLimits] || 0;
    }
    
    // Fallback has no limits
    if (this._isFallback(netloc)) {
      return 0;
    }
    
    this._ensureMethodLimits(netloc, method);
    return this.state_limits[netloc][method][limitType] || defaultLimits[limitType as keyof DefaultLimits] || 0;
  }

  /**
   * Set cooldown for a specific method and netloc
   */
  setCooldown(netloc: string | null, method: string | null, add: number | false): number | false {
    if (!netloc || !method) {
      return false;
    }
    
    const resolvedNetloc = this._resolveNetloc(netloc);
    if (!resolvedNetloc) {
      return false;
    }
    
    // Don't set cooldowns for fallback
    if (this._isFallback(resolvedNetloc)) {
      //console.log(`Fallback URL - no cooldowns applied`);
      return false;
    }
    
    const urlData = this.state.all_urls[resolvedNetloc];
    
    if (add !== false) {
      urlData.cooldown_times[method] = getCurrentTime() + add;
      //console.log(`adding cooldown to ${method} for ${add} seconds`);
      this.saveState();
    }
    
    return this.getCooldownForMethod(resolvedNetloc, method);
  }

  /**
   * Get cooldown for a method on a netloc
   */
  getCooldownForMethod(identifier: string, method: string): number | false {
    const netloc = this._resolveNetloc(identifier);
    if (!netloc) {
      return false;
    }
    
    // Fallback has no cooldowns
    if (this._isFallback(netloc)) {
      return false;
    }
    
    const urlData = this.state.all_urls[netloc];
    
    if (method in urlData.cooldown_times) {
      const waitTime = urlData.cooldown_times[method] - getCurrentTime();
      if (waitTime <= 0) {
        delete urlData.cooldown_times[method];
        //console.log(`removing cooldown for ${method}`);
        this.saveState();
        return false;
      }
      //console.log(`${method} in cooldown for ${waitTime} seconds`);
      return waitTime;
    }
    
    //console.log(`${method} not in cooldown`);
    return false;
  }

  /**
   * Track data size for method
   */
  addData(identifier: string, method: string, dataSize: number): void {
    const netloc = this._resolveNetloc(identifier);
    if (!netloc) {
      return;
    }
    
    // Don't track data for fallback
    if (this._isFallback(netloc)) {
      return;
    }
    
    this._ensureMethodLimits(netloc, method);
    this.state_limits[netloc][method].last_data = dataSize;
    
    if (this.state_limits[netloc][method].avg_data === null) {
      this.state_limits[netloc][method].avg_data = dataSize;
    } else {
      const avg = this.state_limits[netloc][method].avg_data!;
      this.state_limits[netloc][method].avg_data = (avg + dataSize) / 2;
    }
    
    safeDumpToFile(this.state_limits, this.state_limits_file);
  }

  /**
   * Log response data and update state with cooldown times
   */
  logResponse(method?: string, response?: Response, identifier?: string): any {
    method = method || 'default_method';
    
    const netloc = this._resolveNetloc(identifier || this.state.last_url || '');
    if (!netloc) {
      return null;
    }
    
    // Don't log rate limits for fallback
    if (this._isFallback(netloc)) {
      //console.log(`Fallback URL - response logged but no limits tracked`);
      return response;
    }
    
    const urlData = this.state.all_urls[netloc];
    
    this.getHeaders(response!, method, netloc);
    
    const result = response; // Placeholder
    
    if (!(method in urlData.last_mb)) {
      urlData.last_mb[method] = 0;
    }
    
    urlData.last_mb[method] = getJsonSizeInMb(result);
    this.addData(netloc, method, urlData.last_mb[method]);
    
    urlData.rate_limits.push({
      method: method,
      data: urlData.last_mb[method],
      time: getCurrentTime()
    });
    
    this.saveState();
    return result;
  }

  /**
   * Check if method is at rate limit
   */
  isAllLimit(identifier: string, method: string): boolean {
    const netloc = this._resolveNetloc(identifier);
    if (!netloc) {
      return false;
    }
    
    // Fallback is never rate limited
    if (this._isFallback(netloc)) {
      //console.log(`Fallback URL - no rate limits`);
      return false;
    }
    let isLimit:any;
    const urlData = this.state.all_urls[netloc];
    
    if (!(method in urlData.last_mb)) {
      urlData.last_mb[method] = 0;
    }
    
    if (this.getCooldownForMethod(netloc, method)) {
      //console.log(`set_cooldown for method ${method} in ${netloc} hit`);
      return true;
    }
    
    const rateLimit = this.getStateRateLimit(method, netloc, 'rate_limit');
    const rpsLimit = this.getStateRateLimit(method, netloc, 'rps_limit');
    
    urlData.rate_limits = cleanQueries(urlData.rate_limits, 30);
    urlData.rate_limits = urlData.rate_limits.sort((a, b) => a.time - b.time);
    
    this._ensureMethodLimits(netloc, method);
    const avgData = this.state_limits[netloc][method].avg_data || 0;
    if (method =='getDigitalAsset'){
        isLimit = getIsLimit(
      urlData.rate_limits,
      method,
      10,
      50,
      5,
      19,
      69,
      avgData*1.10
    );
    
    }else{
      isLimit = getIsLimit(
        urlData.rate_limits,
        method,
        rateLimit,
        rpsLimit,
        9,
        29,
        99,
        avgData
      );
    }
    if (isLimit !== false) {
      this.setCooldown(netloc, method, Math.max(isLimit, 0));
      return true;
    }
    
    return false;
  }

  /**
   * Get the primary URL based on cooldowns and rate limits (async)
   */
  async asyncGetUrl1(method?: string): Promise<string | null> {
    method = method || 'default_method';
    const primaryNetloc = this._resolveNetloc(this.urls[0]);
    
    if (!primaryNetloc) return null;
    
    const isLimit = this.isAllLimit(primaryNetloc, method);
    this.state.last_method = method;
    this.state.last_url = primaryNetloc;
    
    if (isLimit) {
      const cooldown = this.getCooldownForMethod(primaryNetloc, method);
      if (cooldown !== false) {
        await new Promise(resolve => setTimeout(resolve, cooldown * 1000));
      }
    }
    
    return this._resolveUrl(primaryNetloc);
  }

  /**
   * Get the primary URL based on cooldowns and rate limits (sync)
   */
  getUrl1(method?: string): string | null {
    method = method || 'default_method';
    const primaryNetloc = this._resolveNetloc(this.urls[0]);
    
    if (!primaryNetloc) return null;
    
    const isLimit = this.isAllLimit(primaryNetloc, method);
    this.state.last_method = method;
    this.state.last_url = primaryNetloc;
    
    if (isLimit) {
      const cooldown = this.getCooldownForMethod(primaryNetloc, method);
      if (cooldown !== false) {
        console.warn('Synchronous sleep detected, use asyncGetUrl1 instead');
      }
    }
    
    return this._resolveUrl(primaryNetloc);
  }

  /**
   * Get the appropriate URL based on cooldowns and rate limits
   * Returns fallback if primary is limited
   */
  getUrl(method?: string): string | null {
    method = method || 'default_method';
    const primaryNetloc = this._resolveNetloc(this.urls[0]);
    const fallbackNetloc = this._resolveNetloc(this.fallback_url);
    
    if (!primaryNetloc || !fallbackNetloc) return null;
    
    const isLimit = this.isAllLimit(primaryNetloc, method);
    
    if (isLimit || method === 'get_url2') {
      //console.log(`Using fallback URL (no limits)`);
      this.state.last_url = fallbackNetloc;
    } else {
      this.state.last_method = method;
      this.state.last_url = primaryNetloc;
    }
    
    return this._resolveUrl(this.state.last_url);
  }

  /**
   * Get fallback URL directly (always unlimited)
   */
  getFallbackUrl(): string {
    return this.fallback_url;
  }

  /**
   * Make rate-limited RPC call with automatic URL selection
   */
  async fetchRpc(
    method: string,
    params?: any[],
    id?: string | number,
    jsonrpc?: string | number,
    headers?: Record<string, string>
  ): Promise<any> {
    const primaryNetloc = this._resolveNetloc(this.urls[0]);
    
    if (!primaryNetloc) return null;
    


    const url = this.getUrl(method);
   
    
    if (!url) return null;
    
    this.headers = headers || { 'Content-Type': 'application/json' };
    this.payload = getRpcPayload(method, params || null, id || null, jsonrpc || null);
    
    if (!this.payload) return null;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(this.payload)
    });
    
    const result = this.logResponse(method, response, url);
    
    if (response.status === 429) {
      const retry = getRetryAfter(response);
      if (retry) {
        this.setCooldown(primaryNetloc, method, retry);
      }
      return null;
    }
    
    return result;
  }
}

// Export singleton instance
//export const rateLimiter = RateLimiter.getInstance();